'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

/**
 * Badge Variants
 *
 * Defines visual styles for the Badge component using design tokens.
 * Variants: default, primary, secondary, outline, success, warning, error, info
 * Sizes: sm, md, lg
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 font-medium transition-colors focus:outline-none',
  {
    variants: {
      variant: {
        default:
          'bg-neutral-100 text-neutral-900 border border-neutral-200 dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700',
        primary:
          'bg-primary-100 text-primary-900 border border-primary-200 dark:bg-primary-900 dark:text-primary-100 dark:border-primary-800',
        secondary:
          'bg-neutral-200 text-neutral-700 border border-neutral-300 dark:bg-neutral-700 dark:text-neutral-200 dark:border-neutral-600',
        outline:
          'bg-transparent text-neutral-900 border border-neutral-300 dark:text-neutral-100 dark:border-neutral-600',
        success:
          'bg-success-100 text-success-900 border border-success-200 dark:bg-success-900 dark:text-success-100 dark:border-success-800',
        warning:
          'bg-warning-100 text-warning-900 border border-warning-200 dark:bg-warning-900 dark:text-warning-100 dark:border-warning-800',
        error:
          'bg-error-100 text-error-900 border border-error-200 dark:bg-error-900 dark:text-error-100 dark:border-error-800',
        destructive:
          'bg-error-100 text-error-900 border border-error-200 dark:bg-error-900 dark:text-error-100 dark:border-error-800',
        info: 'bg-info-100 text-info-900 border border-info-200 dark:bg-info-900 dark:text-info-100 dark:border-info-800',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-base',
      },
      shape: {
        rounded: 'rounded-md',
        pill: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      shape: 'rounded',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Icon to display before the text */
  icon?: React.ReactNode;
  /** If true, renders with a dot indicator before text */
  dot?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Content of the badge */
  children: React.ReactNode;
}

/**
 * Badge Component
 *
 * A versatile badge component for displaying tags, statuses, and labels.
 * Supports multiple variants, sizes, and shapes. Integrates with design tokens.
 *
 * @example
 * ```tsx
 * // Basic badge
 * <Badge variant="success">Completed</Badge>
 *
 * // With icon
 * <Badge variant="warning" icon={<AlertIcon />}>
 *   Pending
 * </Badge>
 *
 * // With dot indicator
 * <Badge variant="info" dot>
 *   In Progress
 * </Badge>
 *
 * // Pill-shaped
 * <Badge variant="success" shape="pill">
 *   New
 * </Badge>
 *
 * // Removable tag
 * <Badge onRemove={() => handleRemove()}>
 *   Tag Name
 * </Badge>
 * ```
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      icon,
      dot = false,
      onRemove,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size, shape }), className)}
        {...props}
      >
        {/* Dot Indicator */}
        {dot && (
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-70"
            aria-hidden="true"
          />
        )}

        {/* Icon */}
        {icon && <span className="inline-flex flex-shrink-0">{icon}</span>}

        {/* Content */}
        <span>{children}</span>

        {/* Remove Button */}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-0.5 inline-flex flex-shrink-0 rounded-sm transition-opacity hover:opacity-70 focus:outline-none"
            aria-label="Remove"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
